import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import PageHero from "@/components/PageHero";
import api from "@/lib/api";
import { toDownloadUrl, resolveMediaUrl } from "@/lib/download";
import { Calendar, MapPin, Trophy, Filter, ArrowLeft, Camera, Download, Folder, FolderOpen, ChevronRight, Medal, ListOrdered } from "lucide-react";

export function ChampionshipsList() {
  const [items, setItems] = useState([]);
  const [level, setLevel] = useState("");
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const activeYear = params.get("year") || "";

  useEffect(() => {
    setLoading(true);
    api.get("/championships", { params: { level: level || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [level]);

  // Group championships by year (derived from start_date)
  const byYear = useMemo(() => {
    const map = {};
    items.forEach((c) => {
      const y = (c.start_date || "").slice(0, 4) || "Undated";
      if (!map[y]) map[y] = [];
      map[y].push(c);
    });
    return map;
  }, [items]);

  const years = useMemo(
    () => Object.keys(byYear).sort((a, b) => (b > a ? 1 : -1)),
    [byYear]
  );

  const shown = activeYear ? { [activeYear]: byYear[activeYear] || [] } : byYear;
  const shownYears = activeYear ? [activeYear] : years;

  const setYear = (y) => {
    if (y) params.set("year", y); else params.delete("year");
    setParams(params, { replace: true });
  };

  return (
    <div data-testid="championships-list">
      <PageHero eyebrow="Championships" title="National & International Calendar" subtitle="Browse official NKF Bharat tournaments — from district-level qualifiers to international friendship cups." />
      <section className="section-spacing nkf-container">
        {/* Breadcrumb */}
        <nav className="text-[11px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-1.5 mb-4" aria-label="Breadcrumb">
          <span className="text-nkf-blue font-semibold">Championships</span>
          {activeYear && (
            <>
              <span className="opacity-60">/</span>
              <button onClick={() => setYear("")} className="hover:text-nkf-blue">All Years</button>
              <span className="opacity-60">/</span>
              <span className="text-nkf-blue font-semibold">{activeYear}</span>
            </>
          )}
        </nav>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          {["", "national", "state", "district", "zonal", "international"].map((l) => (
            <button key={l || "all"} onClick={() => setLevel(l)} data-testid={`level-${l || "all"}`}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-full border transition ${level === l ? "bg-nkf-blue text-white border-nkf-blue" : "bg-white border-slate-200 text-slate-700 hover:border-nkf-blue/40"}`}>
              {l || "All Levels"}
            </button>
          ))}
        </div>

        {/* Year folders */}
        {!activeYear && years.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Year:</span>
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                data-testid={`year-folder-${y}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-widest rounded-full border bg-white border-slate-200 text-slate-700 hover:border-nkf-gold/60 hover:text-nkf-navy transition"
              >
                <Folder className="h-3.5 w-3.5 text-nkf-gold" /> {y}
                <span className="text-slate-400">({byYear[y].length})</span>
              </button>
            ))}
          </div>
        )}

        {loading ? <div className="text-slate-500">Loading championships…</div> : (
          <div className="space-y-10">
            {shownYears.map((y) => (
              <div key={y} data-testid={`year-section-${y}`}>
                <div className="flex items-baseline gap-3 mb-4">
                  <FolderOpen className="h-5 w-5 text-nkf-gold" />
                  <h2 className="font-display text-2xl text-nkf-blue">{y}</h2>
                  <span className="text-xs uppercase tracking-widest text-slate-500">{(shown[y] || []).length} championship{(shown[y] || []).length === 1 ? "" : "s"}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {(shown[y] || []).map((c) => (
                    <Link to={`/championships/${c.id}`} key={c.id} className="federation-card bg-white border border-slate-200 rounded-md overflow-hidden flex flex-col" data-testid={`championship-card-${c.id}`}>
                      <div className="relative aspect-video bg-slate-100">
                        {c.poster_url ? <img src={c.poster_url} alt={c.title} className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center text-slate-400"><Trophy /></div>}
                        <div className="absolute top-3 left-3 bg-nkf-blue text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded">{c.level}</div>
                        <div className="absolute top-3 right-3 bg-white/90 text-nkf-navy text-[10px] uppercase tracking-widest px-2 py-1 rounded flex items-center gap-1"><Folder className="h-3 w-3 text-nkf-gold" />{y}</div>
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="font-display text-lg text-nkf-blue leading-snug line-clamp-2">{c.title}</h3>
                        {c.discipline && <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{c.discipline}</div>}
                        <div className="mt-3 space-y-1 text-sm text-slate-600 flex-1">
                          <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-nkf-red" /> {c.start_date} – {c.end_date}</div>
                          <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-nkf-red" /> {c.location}</div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-2">
                          <StatusPill status={registrationStatus(c)} />
                          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-nkf-red font-semibold">
                            Open Folder <ChevronRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {!(shown[y] || []).length && <div className="col-span-full p-8 text-center text-slate-500 border border-dashed rounded-md">No events found.</div>}
                </div>
              </div>
            ))}
            {!shownYears.length && <div className="p-8 text-center text-slate-500 border border-dashed rounded-md">No championships yet.</div>}
          </div>
        )}
      </section>
    </div>
  );
}

export function ChampionshipDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [results, setResults] = useState({ total: 0, grouped: [] });
  useEffect(() => {
    api.get(`/championships/${id}`).then((r) => setC(r.data)).catch(() => setC(false));
    api.get("/media", { params: { championship_id: id } }).then((r) => setGallery(r.data || [])).catch(() => setGallery([]));
    api.get(`/championships/${id}/results`).then((r) => setResults(r.data || { total: 0, grouped: [] })).catch(() => setResults({ total: 0, grouped: [] }));
  }, [id]);
  if (c === null) return <div className="nkf-container py-20 text-slate-500">Loading…</div>;
  if (c === false) return <div className="nkf-container py-20 text-slate-500">Championship not found.</div>;
  const year = (c.start_date || "").slice(0, 4) || "—";
  return (
    <div data-testid="championship-detail">
      <section className="bg-nkf-blue text-white relative overflow-hidden">
        <div className="absolute inset-0 diagonal-stripes pointer-events-none" />
        <div className="nkf-container py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end relative">
          <div className="lg:col-span-7">
            <nav className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold/90 flex items-center gap-1.5 mb-3" aria-label="Breadcrumb">
              <Link to="/championships" className="hover:text-nkf-gold">Championships</Link>
              <span className="opacity-60">/</span>
              <Link to={`/championships?year=${year}`} className="hover:text-nkf-gold">{year}</Link>
              <span className="opacity-60">/</span>
              <span className="text-white/90 truncate">{c.title}</span>
            </nav>
            <div className="text-[11px] uppercase tracking-[0.3em] text-nkf-gold flex flex-wrap items-center gap-x-3 gap-y-1">
              <span data-testid="detail-level">{c.level} championship</span>
              {c.discipline && <><span className="opacity-40">·</span><span data-testid="detail-discipline">{c.discipline}</span></>}
            </div>
            <h1 className="font-display text-3xl md:text-5xl mt-2 leading-tight">{c.title}</h1>
            <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/85">
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-nkf-gold" /> {c.start_date} – {c.end_date}</div>
              <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-nkf-gold" /> {c.location}</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded overflow-hidden border border-white/15 bg-white/5">
              {c.poster_url ? <img src={c.poster_url} alt={c.title} className="w-full aspect-video object-cover" /> :
                <div className="aspect-video flex items-center justify-center text-white/50"><Trophy /></div>}
            </div>
          </div>
        </div>
      </section>
      <section className="nkf-container py-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="detail-meta">
            <MetaTile label="Type" value={`${cap(c.level)} Championship`} />
            {c.discipline && <MetaTile label="Discipline" value={c.discipline} />}
            <MetaTile label="State" value={c.state || "—"} />
            <MetaTile label="Dates" value={`${c.start_date} → ${c.end_date}`} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-nkf-blue">About the Event</h2>
            <p className="text-slate-700 mt-2 leading-relaxed">{c.description || "Official NKF Bharat championship under the federation calendar."}</p>
          </div>
          <div>
            <h3 className="font-display text-xl text-nkf-blue">Categories</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {(c.categories || []).map((cat) => (
                <span key={cat} className="text-xs uppercase tracking-widest bg-slate-100 text-slate-700 px-3 py-1 rounded">{cat}</span>
              ))}
              {!(c.categories || []).length && <span className="text-sm text-slate-400">Categories will be announced by the federation secretariat.</span>}
            </div>
          </div>
        </div>
        <aside className="bg-slate-50 border border-slate-200 rounded-md p-5 self-start" data-testid="registration-status-card">
          <div className="text-[11px] uppercase tracking-widest text-nkf-red font-semibold">Registration</div>
          {(() => {
            const s = registrationStatus(c);
            return (
              <>
                <div className={`font-display text-2xl mt-2 ${s.tone}`} data-testid="registration-status">{s.label}</div>
                {s.subtext && <div className="text-sm text-slate-600 mt-1" data-testid="registration-subtext">{s.subtext}</div>}
              </>
            );
          })()}
          <div className="mt-4 text-xs text-slate-500">Entries must be submitted by your affiliated academy or state unit. Confirmation issued by the federation secretariat upon verification.</div>
        </aside>
      </section>

      {/* Official Results */}
      {results.total > 0 && (
        <section className="nkf-container pb-10" data-testid="championship-public-results">
          <div className="flex items-center gap-3 mb-4">
            <ListOrdered className="h-5 w-5 text-nkf-red" />
            <h2 className="font-display text-2xl text-nkf-blue">Official Results</h2>
            <span className="text-xs uppercase tracking-widest text-slate-500">
              {results.total} placement{results.total === 1 ? "" : "s"} · {results.grouped.length} categor{results.grouped.length === 1 ? "y" : "ies"}
            </span>
          </div>
          <ResultsBoard grouped={results.grouped} />
        </section>
      )}

      {/* Photo gallery */}
      {gallery.length > 0 && (
        <section className="nkf-container pb-16" data-testid="championship-public-gallery">
          <div className="flex items-center gap-3 mb-4">
            <Camera className="h-5 w-5 text-nkf-red" />
            <h2 className="font-display text-2xl text-nkf-blue">Event Gallery</h2>
            <span className="text-xs uppercase tracking-widest text-slate-500">{gallery.length} photo{gallery.length === 1 ? "" : "s"}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {gallery.map((m) => (
              <a
                key={m.id}
                href={toDownloadUrl(m.media_url, `${(c.title || "photo").replace(/\s+/g, "_")}_${m.id}.jpg`)}
                download
                className="relative group border border-slate-200 rounded-md overflow-hidden block"
                data-testid={`championship-public-photo-${m.id}`}
                title="Download watermarked photo"
              >
                <img src={resolveMediaUrl(m.media_url)} alt={m.title || c.title} className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-nkf-navy/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition flex items-end justify-end p-2">
                  <span className="inline-flex items-center gap-1 bg-white/95 text-nkf-navy text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded">
                    <Download className="h-3 w-3" /> Download
                  </span>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function cap(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

function StatusPill({ status }) {
  const bg = {
    "text-nkf-blue":    "bg-nkf-blue/10 text-nkf-blue border-nkf-blue/20",
    "text-emerald-700": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "text-red-700":     "bg-red-100 text-red-800 border-red-200",
    "text-slate-500":   "bg-slate-100 text-slate-600 border-slate-200",
  }[status.tone] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border ${bg}`} data-testid="card-registration-status">
      {status.label}
    </span>
  );
}

function MetaTile({ label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-nkf-blue font-semibold mt-0.5 truncate" title={value}>{value}</div>
    </div>
  );
}

function ResultsBoard({ grouped }) {
  const [q, setQ] = useState("");
  const [eventType, setEventType] = useState("");
  const eventTypes = useMemo(() => {
    const s = new Set();
    grouped.forEach((g) => g.event_type && s.add(g.event_type));
    return Array.from(s);
  }, [grouped]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return grouped
      .filter((g) => !eventType || g.event_type === eventType)
      .map((g) => ({
        ...g,
        rows: g.rows.filter((r) => !needle ||
          (r.athlete_name || "").toLowerCase().includes(needle) ||
          (r.district || "").toLowerCase().includes(needle) ||
          (r.academy || "").toLowerCase().includes(needle) ||
          (g.category || "").toLowerCase().includes(needle)
        ),
      }))
      .filter((g) => g.rows.length > 0);
  }, [grouped, q, eventType]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search athlete, district, academy or category…"
          data-testid="results-search"
          className="flex-1 min-w-[220px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-nkf-blue/30 focus:border-nkf-blue"
        />
        {eventTypes.length > 1 && (
          <div className="flex items-center gap-1" data-testid="results-event-filter">
            <button
              onClick={() => setEventType("")}
              className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded ${!eventType ? "bg-nkf-blue text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-nkf-blue/40"}`}
            >All</button>
            {eventTypes.map((et) => (
              <button
                key={et}
                onClick={() => setEventType(et)}
                className={`text-[11px] uppercase tracking-widest font-bold px-3 py-2 rounded ${eventType === et ? "bg-nkf-blue text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-nkf-blue/40"}`}
              >{et}</button>
            ))}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-6 text-center">No results match your filters.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((g, i) => (
            <div key={i} className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid={`results-group-${i}`}>
              <div className="bg-nkf-blue text-white px-4 py-2.5 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-display text-sm truncate">{g.category}</div>
                  {g.event_type && <div className="text-[10px] uppercase tracking-[0.3em] text-nkf-gold">{g.event_type}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-widest text-nkf-gold">{g.rows.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {g.rows.map((r) => (
                  <li key={r.id} className="flex items-center gap-3 px-3 py-2">
                    <ResultsPositionBadge pos={r.position} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 truncate">{r.athlete_name}</div>
                      {(r.district || r.academy) && (
                        <div className="text-[11px] text-slate-500 truncate">{[r.district, r.academy].filter(Boolean).join(" · ")}</div>
                      )}
                    </div>
                    {r.certificate_number && (
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{r.certificate_number}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResultsPositionBadge({ pos }) {
  if (!pos) return null;
  const p = String(pos).trim().toUpperCase();
  const map = {
    GOLD: "bg-nkf-gold/20 text-nkf-navy border-nkf-gold",
    SILVER: "bg-slate-200 text-slate-700 border-slate-300",
    BRONZE: "bg-amber-100 text-amber-800 border-amber-300",
    I: "bg-nkf-gold/20 text-nkf-navy border-nkf-gold",
    II: "bg-slate-200 text-slate-700 border-slate-300",
    III: "bg-amber-100 text-amber-800 border-amber-300",
    "1": "bg-nkf-gold/20 text-nkf-navy border-nkf-gold",
    "2": "bg-slate-200 text-slate-700 border-slate-300",
    "3": "bg-amber-100 text-amber-800 border-amber-300",
  };
  const cls = map[p] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border rounded px-1.5 py-0.5 min-w-[42px] justify-center ${cls}`}>
      <Medal className="h-3 w-3" /> {p}
    </span>
  );
}

// Parse YYYY-MM-DD as a local date at midnight (avoids timezone drift).
function parseDate(s) {
  if (!s || typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function registrationStatus(c) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const deadline = parseDate(c.registration_deadline);
  const start = parseDate(c.start_date);
  const end = parseDate(c.end_date);

  // Event already finished
  if (end && today > end) {
    return { label: "Event Concluded", tone: "text-slate-500", subtext: `Ended ${c.end_date}` };
  }
  // Event is running right now
  if (start && end && today >= start && today <= end) {
    return { label: "Event Live", tone: "text-emerald-700", subtext: `${c.start_date} – ${c.end_date}` };
  }
  // Deadline has passed but the event hasn't started yet
  if (deadline && today > deadline) {
    return { label: "Registration Closed", tone: "text-red-700", subtext: `Deadline was ${c.registration_deadline}` };
  }
  // Deadline is set and still ahead
  if (deadline) {
    const days = Math.round((deadline - today) / 86400000);
    return {
      label: "Open Now",
      tone: "text-nkf-blue",
      subtext: days === 0
        ? `Closing today · ${c.registration_deadline}`
        : `Closes in ${days} day${days === 1 ? "" : "s"} · ${c.registration_deadline}`,
    };
  }
  // No deadline set — general open status
  return { label: "Open", tone: "text-nkf-blue", subtext: "Deadline to be announced" };
}
